import {
  claimSearchQuerySchema,
  claimSearchResponseSchema,
  claimDetailResponseSchema,
  type ClaimSearchQuery,
} from "@broadview/contracts";
import { clientError } from "./errors";
import { createLiveTransport } from "./transport";
import type { BroadviewClientOptions, TenantScopedRequest } from "./client";

/** Live-only, read-only canonical evidence. Never substitutes prototype fixtures. */
export function createClaimEvidenceClient(options: BroadviewClientOptions) {
  if (options.mode === "fixture" || !options.apiOrigin || !options.getAccessToken) {
    throw clientError(
      503,
      "LIVE_ADAPTER_DISABLED",
      "Claim evidence requires a live API origin and authenticated token reader.",
    );
  }
  const getJson = createLiveTransport({
    apiOrigin: options.apiOrigin,
    getAccessToken: options.getAccessToken,
    fetchImpl: options.fetchImpl,
  });
  return {
    search: async ({ tenantId, ...input }: TenantScopedRequest & Partial<ClaimSearchQuery>) => {
      const query = claimSearchQuerySchema.parse(input);
      const parameters = new URLSearchParams({
        limit: String(query.limit),
        classification: query.classification,
      });
      if (query.search) parameters.set("search", query.search);
      if (query.after) parameters.set("after", query.after);
      const response = await getJson(
        { path: `/claims?${parameters.toString()}`, tenantId },
        (payload) => claimSearchResponseSchema.parse(payload),
      );
      if (
        response.tenantId !== tenantId ||
        JSON.stringify(response.query) !== JSON.stringify(query)
      ) {
        throw clientError(
          502,
          "CONTRACT_MISMATCH",
          "Claim evidence did not match the selected tenant or query.",
        );
      }
      return response;
    },
    get: async ({ tenantId, id }: TenantScopedRequest & { id: string }) => {
      const response = await getJson(
        { path: `/claims/${encodeURIComponent(id)}`, tenantId },
        (payload) => claimDetailResponseSchema.parse(payload),
      );
      if (response.tenantId !== tenantId || response.item.id !== id) {
        throw clientError(
          502,
          "CONTRACT_MISMATCH",
          "Claim evidence did not match the selected tenant or record.",
        );
      }
      return response;
    },
  };
}
